import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Package, Filter, Calendar, ChevronRight, Printer, FileDown, Send } from 'lucide-react';
import { clientService, productService, api, delayedOrdersReportService } from '../services/api';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';
import PrintableOrderMatrix from '../components/PrintableOrderMatrix';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const statusConfig = {
  completed: { label: 'Consegnato', bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', dot: 'bg-emerald-500' },
  on_time: { label: 'In consegna', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300', dot: 'bg-amber-500' },
  delayed: { label: 'In ritardo', bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-300', dot: 'bg-red-500' },
};

const StatusBadge = ({ status, days }) => {
  const cfg = statusConfig[status] || statusConfig.on_time;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
      {cfg.label}{status === 'delayed' && days > 0 ? ` · ${days}gg` : ''}
    </span>
  );
};

export default function OrderProductMatrixPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ products: [], client_name: null });
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  // Filters
  const [clientId, setClientId] = useState('');
  const [productId, setProductId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [delayedOnly, setDelayedOnly] = useState(false);

  // Print
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Vista_Ordini_per_Prodotto',
    pageStyle: '@page { size: A4 landscape; margin: 8mm; }',
  });

  const handleExportPdf = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const mod = await import('html2pdf.js');
      const html2pdf = mod.default || mod;
      const clientSlug = (data.client_name || 'tutti-i-clienti').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const datestr = new Date().toISOString().slice(0, 10);
      await html2pdf()
        .set({
          margin: 6,
          filename: `vista-ordini-prodotto_${clientSlug}_${datestr}.pdf`,
          image: { type: 'jpeg', quality: 0.97 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
          pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', 'thead'] },
        })
        .from(printRef.current)
        .save();
      toast.success('PDF esportato');
    } catch (e) {
      toast.error('Errore durante esportazione PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleSendReport = async () => {
    if (!window.confirm('Vuoi inviare ora via email il report degli ordini in ritardo ai destinatari configurati?')) return;
    setSendingReport(true);
    try {
      const res = await delayedOrdersReportService.sendNow();
      const data = res.data;
      const sent = data.sent_to?.length || 0;
      const failed = data.failed?.length || 0;
      if (failed === 0) toast.success(`Report inviato a ${sent} destinatari`);
      else toast.warning(`Report inviato a ${sent} destinatari, ${failed} falliti`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio report - configura i destinatari in Impostazioni Email');
    } finally {
      setSendingReport(false);
    }
  };

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (clientId) params.client_id = clientId;
      if (productId) params.product_id = productId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (orderStatus) params.order_status = orderStatus;
      if (delayedOnly) params.delayed_only = true;

      const res = await api.get('/orders/product-matrix', { params });
      setData(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento della matrice');
    } finally {
      setLoading(false);
    }
  }, [clientId, productId, dateFrom, dateTo, orderStatus, delayedOnly]);

  // Load clients once
  useEffect(() => {
    clientService.getAll()
      .then(r => setClients(r.data || []))
      .catch(() => {});
  }, []);

  // Load products — dependent on selected client
  useEffect(() => {
    const params = {};
    if (clientId) params.client_id = clientId;
    productService.getAll(params)
      .then(r => {
        const list = r.data || [];
        setProducts(list);
        // Clear product filter if currently selected product not in new list
        if (productId && !list.some(p => p.id === productId)) {
          setProductId('');
        }
      })
      .catch(() => {});
  }, [clientId, productId]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  const title = data.client_name
    ? `Vista ordini per prodotto - ${data.client_name}`
    : 'Vista ordini per prodotto - Tutti i clienti';

  return (
    <div className="space-y-6" data-testid="order-product-matrix-page">
      {/* Hidden print area */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '297mm' }}>
        <PrintableOrderMatrix
          ref={printRef}
          products={data.products}
          clientName={data.client_name}
          filters={{
            clientLabel: clientId ? (clients.find(c => c.id === clientId)?.name || 'Cliente') : 'Tutti',
            productLabel: productId ? (products.find(p => p.id === productId)?.name ? `${products.find(p => p.id === productId)?.code} - ${products.find(p => p.id === productId)?.name}` : 'Prodotto') : 'Tutti',
            dateFrom,
            dateTo,
            delayedOnly,
          }}
        />
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="matrix-title">{title}</h1>
          <p className="text-slate-500 mt-1">Stato consegne ai clienti — quantita ordinate, consegnate e residue</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSendReport}
            disabled={sendingReport}
            className="h-10 px-4 border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="send-delayed-report-btn"
            title="Invia il report degli ordini in ritardo via email"
          >
            <Send className="w-4 h-4 mr-2" />
            {sendingReport ? 'Invio...' : 'Invia Report Email'}
          </Button>
          <Button variant="outline" onClick={handlePrint} className="h-10 px-4" disabled={loading || !data.products?.length} data-testid="print-matrix-btn">
            <Printer className="w-4 h-4 mr-2" />
            Stampa vista
          </Button>
          <Button onClick={handleExportPdf} className="h-10 px-4" disabled={loading || exporting || !data.products?.length} data-testid="export-pdf-btn">
            <FileDown className="w-4 h-4 mr-2" />
            {exporting ? 'Esportazione...' : 'Esporta PDF'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filtri</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Cliente</Label>
              <Select value={clientId || 'all'} onValueChange={v => setClientId(v === 'all' ? '' : v)} data-testid="filter-client">
                <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i clienti</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Prodotto {clientId && <span className="text-sky-600">(filtrato per cliente)</span>}</Label>
              <Select value={productId || 'all'} onValueChange={v => setProductId(v === 'all' ? '' : v)} data-testid="filter-product">
                <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{clientId ? 'Tutti i prodotti del cliente' : 'Tutti i prodotti'}</SelectItem>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{`${p.code} - ${p.name}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data consegna da</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data consegna a</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="filter-date-to" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Stato ordine</Label>
              <Select value={orderStatus || 'all'} onValueChange={v => setOrderStatus(v === 'all' ? '' : v)} data-testid="filter-status">
                <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="pending">In attesa</SelectItem>
                  <SelectItem value="confirmed">Confermato</SelectItem>
                  <SelectItem value="in_production">In produzione</SelectItem>
                  <SelectItem value="completed">Completato</SelectItem>
                  <SelectItem value="shipped">Spedito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-2 pb-1">
                <Switch checked={delayedOnly} onCheckedChange={setDelayedOnly} id="delayed-only" data-testid="filter-delayed-only" />
                <Label htmlFor="delayed-only" className="text-xs text-red-600 font-medium cursor-pointer">Solo in ritardo</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Matrix */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-slate-800 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : data.products.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Nessun ordine trovato con i filtri selezionati</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.products.map(product => (
            <Card key={product.product_id} className="overflow-hidden" data-testid={`product-row-${product.product_code}`}>
              {/* Product Header */}
              <div
                className="flex items-center justify-between p-4 bg-slate-50 border-b cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => navigate(`/products/${product.product_id}`)}
                data-testid={`product-header-${product.product_code}`}
              >
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-slate-600" />
                  <div>
                    <span className="font-mono font-bold text-slate-900">{product.product_code}</span>
                    <span className="text-slate-500 ml-2">{product.product_name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="text-xs text-slate-400 uppercase">Ordinato</p>
                    <p className="font-bold text-slate-800">{product.total_ordered}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 uppercase">Consegnato</p>
                    <p className={`font-bold ${product.total_overdelivery > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{product.total_delivered}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 uppercase">Residuo</p>
                    <p className={`font-bold ${product.total_remaining > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{product.total_remaining}</p>
                  </div>
                  {product.total_overdelivery > 0 && (
                    <div className="text-center" data-testid={`product-overdelivery-${product.product_id}`}>
                      <p className="text-xs text-amber-500 uppercase">Sovraconsegna</p>
                      <p className="font-bold text-amber-700">+{product.total_overdelivery}</p>
                    </div>
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>

              {/* Order Lines Table */}
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-white">
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ordine</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Qta Ord.</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Cons. / Residuo</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Sovracons.</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Consegna Richiesta</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Ultima Consegna</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Stato</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.order_lines.map(line => {
                      const isDelayed = line.delay_status === 'delayed';
                      const isCompleted = line.delay_status === 'completed';
                      const rowCls = isDelayed
                        ? 'bg-red-50/60 hover:bg-red-50'
                        : isCompleted
                          ? 'bg-emerald-50/40 hover:bg-emerald-50'
                          : 'bg-amber-50/30 hover:bg-amber-50';
                      return (
                        <tr
                          key={line.line_id}
                          className={`border-b last:border-0 cursor-pointer transition-colors ${rowCls}`}
                          onClick={() => navigate(`/orders/${line.order_id}`)}
                          data-testid={`line-${line.line_id}`}
                        >
                          <td className="px-4 py-2.5">
                            <span className="font-mono font-medium text-slate-800">{line.order_number}</span>
                            {line.customer_order_ref && <span className="text-xs text-slate-400 ml-2">({line.customer_order_ref})</span>}
                          </td>
                          <td className="px-4 py-2.5 text-slate-600">{line.client_name}</td>
                          <td className="px-4 py-2.5 text-center font-medium">{line.quantity}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className="font-mono">
                              <span className={`font-bold ${line.overdelivery_quantity > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{line.delivered_quantity}</span>
                              <span className="text-slate-400 mx-1">/</span>
                              <span className={`font-bold ${line.remaining_quantity > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{line.remaining_quantity}</span>
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {line.overdelivery_quantity > 0 ? (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 font-mono" data-testid={`line-overdelivery-${line.line_id}`}>+{line.overdelivery_quantity}</Badge>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              <span className={isDelayed ? 'text-red-700 font-medium' : 'text-slate-600'}>{fmtDate(line.delivery_date)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-center text-slate-600">{fmtDate(line.last_delivery_date)}</td>
                          <td className="px-4 py-2.5 text-center">
                            <StatusBadge status={line.delay_status} days={line.delay_days} />
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <ChevronRight className="w-4 h-4 text-slate-300 inline" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
